import { Add, Edit, EventAvailable, EventBusy } from '@mui/icons-material';
import { Table, TableBody, TableRow, TableCell, TableContainer, TableHead, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DonorSubscriptionResponse } from 'src/modules/gift/models/donor';
import { formatDate } from 'src/shared/functions/formatDate';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { DonorSubscriptionsCreateModal } from './donorSubscriptionsCreateModal';
import { DonorSubscriptionsEditModal } from './donorSubscriptionsEditModal';

interface DonorDetailSubscriptionsProps {
  donorId: number;
  subscriptions: DonorSubscriptionResponse[];
}

export const DonorDetailSubscriptions = (props: DonorDetailSubscriptionsProps) => {
  const { donorId, subscriptions } = props;
  const { t } = useTranslation(['gift']);
  const { setDateStamp } = useGlobals();
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedDonorSubscription, setSelectedDonorSubscription] = useState<DonorSubscriptionResponse>();

  const emptyCell = <Typography sx={{ color: (thm) => thm.palette.grey[300] }}> &mdash;</Typography>;

  const translateFrequency = (frequency: number) => {
    switch (frequency) {
      case 1:
        return t('gift:donorSubscriptions.monthly');
      case 3:
        return t('gift:donorSubscriptions.quarterly');
      case 12:
        return t('gift:donorSubscriptions.annually');
      default:
        return '';
    }
  };

  const handleEditClick = (item: DonorSubscriptionResponse) => {
    ConsoleHelper.log('handleEditClick', item);
    setSelectedDonorSubscription(item);
    setEditMode(true);
  };

  return (
    <ContentSection
      header={t('gift:donorsDetailPage.donorSubscriptions')}
      showHeaderCounter
      headerCounter={(subscriptions && subscriptions?.length) ?? 0}
      button={
        <Button variant='outlined' size='small' endIcon={<Add fontSize='small' />} onClick={() => setCreateMode(true)}>
          {t('common:button.add')}
        </Button>
      }
    >
      {subscriptions && subscriptions.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '1rem' }} />
                <TableCell>{t('gift:paymentDetail.subscriptionStart')}</TableCell>
                <TableCell align='right' sx={{ pr: '1.5rem !important', width: '3rem', whiteSpace: 'nowrap' }}>
                  {t('gift:paymentDetail.amount')}
                </TableCell>
                <TableCell>{t('gift:paymentDetail.paymentFrequency')}</TableCell>
                <TableCell>{t('gift:paymentDetail.subscriptionLength')}</TableCell>
                <TableCell>{t('gift:paymentDetail.latestInvoice')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((item) => {
                return (
                  <TableRow
                    key={item.id}
                    sx={{
                      '& .MuiTableCell-root': {
                        ...(!item.active
                          ? {
                              color: 'grey.500',
                              textDecoration: 'line-through',
                            }
                          : null),
                      },
                    }}
                  >
                    <TableCell>
                      {item.active ? (
                        <Tooltip title={t('gift:paymentDetail.active')} arrow placement='top'>
                          <div>
                            <EventAvailable color='success' fontSize='small' />
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('gift:paymentDetail.inactive')} arrow placement='top'>
                          <div>
                            <EventBusy color='disabled' fontSize='small' />
                          </div>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(item.subscriptionStart)}</TableCell>
                    {/* <TableCell>{item.fundRaiser_id}</TableCell> */}
                    <TableCell align='right' sx={{ pr: '1.5rem !important', whiteSpace: 'nowrap' }}>
                      {<AmountWithCurrency amount={item.amount} /> || emptyCell}
                    </TableCell>
                    <TableCell>{translateFrequency(item.monthlyFrequency)}</TableCell>
                    <TableCell>{item.continuous ? t('gift:paymentDetail.continuous') : item.subscriptionLength}</TableCell>
                    <TableCell>{formatDate(item.lastInvoiceCreated)}</TableCell>
                    <TableCell padding='none' align='right' sx={{ p: '0 !important' }}>
                      <IconButton size='small' onClick={() => handleEditClick(item)}>
                        <Edit fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography sx={{ color: 'grey.500', py: 1 }}>{t('gift:donorsDetailPage.noDonorSubscriptions')}</Typography>
      )}
      <DonorSubscriptionsCreateModal
        onClose={() => setCreateMode(false)}
        onSave={() => {
          setCreateMode(false);
          setDateStamp(new Date());
        }}
        modalOpen={createMode}
        donorId={donorId}
      />
      {selectedDonorSubscription && editMode && (
        <DonorSubscriptionsEditModal
          onClose={() => setEditMode(false)}
          onSave={() => {
            setEditMode(false);
            setDateStamp(new Date());
          }}
          modalOpen={editMode}
          donorSubscriptionId={selectedDonorSubscription.id}
        />
      )}
    </ContentSection>
  );
};
